import React, { FC, memo, useRef, useState } from 'react';
import { UseFormRegister } from 'react-hook-form';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import useOnClickOutside from 'common/hooks/useClickOutside';
import { StyledInput } from './styled';
import 'react-phone-input-2/lib/material.css';

interface InputPhoneProps {
  id: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  errorMessage?: string;
  hint?: string;
  register: UseFormRegister<any>;
  value?: number | string;
  isValid: boolean;
  allowChangeDialCode?: boolean;
  hideCountryCode?: boolean;
  onChange?: (e: string, dialCode: string) => void;
}

/**
 * A React component that renders a phone input field using the react-phone-input-2 library.
 *
 * @param id - The ID of the input field.
 * @param placeholder - The placeholder text for the input field.
 * @param label - The label text for the input field.
 * @param disabled - Whether the input field is disabled or not.
 * @param errorMessage - An error message to display if the input field has an invalid value.
 * @param hint - A hint to display below the input field.
 * @param register - A function from react-hook-form to register the input field with form validation.
 * @param value - The current value of the input field.
 * @param isValid - Whether the input field's current value is valid or not.
 * @param allowChangeDialCode - Whether the user can change the dial code in the input field.
 * @param hideCountryCode - Whether to hide the country code dropdown in the input field.
 * @param onChange - A callback function that is called when the input field's value changes.
 */
const InputPhone: FC<InputPhoneProps> = ({
  id,
  placeholder,
  label,
  disabled,
  errorMessage,
  hint,
  register,
  value,
  isValid,
  allowChangeDialCode = false,
  hideCountryCode = false,
  onChange,
}) => {
  const ref = useRef(null);
  const [isActive, setIsActive] = useState(false);

  useOnClickOutside(ref, () => setIsActive(false));
  return (
    <StyledInput
      isActive={isActive}
      isError={!!errorMessage || (!!value && !isValid)}
      disabled={disabled}
    >
      <label>{label}</label>
      <section ref={ref} onMouseDown={() => setIsActive(true)}>
        <PhoneInput
          placeholder={placeholder}
          specialLabel={''}
          country={'pt'}
          {...register(id)}
          value={String(value)}
          onChange={(e, data) => {
            if (hideCountryCode) {
              const dataCode = data as CountryData;

              onChange && onChange(e, dataCode?.dialCode || '');
            } else {
              register &&
                register(id).onChange({
                  target: { value: e, name: id },
                  type: 'change',
                });
            }
          }}
          enableSearch={true}
          searchPlaceholder={'Search by countries'}
          countryCodeEditable={!!allowChangeDialCode}
          disabled={disabled}
        />
      </section>
      <span>{errorMessage ? errorMessage : hint}</span>
    </StyledInput>
  );
};

export default memo(InputPhone);
